/** Size an element so it ends exactly at the bottom of the window.
 *
 * The replay board is a two-pane layout — chain one side, basket the other
 * — and the whole point is that stepping the clock never moves the page.
 * That only holds if the PAGE does not scroll: an outer scrollbar plus an
 * inner one is worse than the stacked layout it replaced, because now
 * there are two things to scroll and neither is obviously the right one.
 *
 * ⚠️ MEASURED, NOT A `vh` CONSTANT. The obvious version is
 * `max-h-[calc(100vh-22rem)]`, and it is wrong the moment anything above
 * the board changes height — which it does routinely here: the step
 * buttons wrap to a second row on a narrow window, and the expiry tabs are
 * absent on sessions that archived one expiry. Every one of those shifts
 * the board's top by tens of pixels, and a constant would give back the
 * outer scrollbar.
 *
 * ⚠️⚠️ THE TRAILING GAP COMES FROM THE DOM, NEVER FROM `scrollHeight`.
 * Deriving it from `scrollHeight` is circular and loops forever: once the
 * board fits, the page stops overflowing, so `scrollHeight` clamps to the
 * viewport and the same expression starts reporting the LEFTOVER GAP
 * instead of the content below. Each pass then shrinks the board by
 * another gutter — React caps it at "Maximum update depth exceeded".
 * Measuring it once and caching does not save it either: the first pass
 * runs before the chain query resolves, when the page is short and not
 * overflowing, so the cached value is wrong for the rest of the session.
 * Siblings-after plus the parent's bottom padding is the only reading that
 * does not depend on this element's own height.
 *
 * Below `lg` the board is a single column and a fitted height would make
 * it unusably cramped, so this returns undefined and the page flows.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/** Tailwind's `lg`. Below it the board stacks and should scroll normally. */
const LG = 1024;
/** Never squeeze below this, however little room is left — better to give
 * back the page scrollbar than to render a two-row chain. */
const MIN_HEIGHT = 260;
/** Everything laid out after the board, which by construction does not
 * depend on how tall the board is. */
function trailingBelow(el: HTMLElement): number {
  let total = 0;
  for (let n = el.nextElementSibling; n !== null; n = n.nextElementSibling) {
    total += n.getBoundingClientRect().height;
  }
  const parent = el.parentElement;
  if (parent) total += parseFloat(getComputedStyle(parent).paddingBottom) || 0;
  return total;
}

export function useFitToViewport(): {
  ref: React.RefObject<HTMLDivElement>;
  height: number | undefined;
} {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  const measure = useCallback((): void => {
    const el = ref.current;
    if (!el) return;
    if (window.innerWidth < LG) {
      setHeight(undefined);
      return;
    }
    const top = el.getBoundingClientRect().top;
    const next = Math.max(MIN_HEIGHT, window.innerHeight - top - trailingBelow(el));
    setHeight((current) =>
      current !== undefined && Math.abs(current - next) < 2 ? current : next,
    );
  }, []);

  useLayoutEffect(measure);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  return { ref, height };
}
